import React, { useState, useCallback } from 'react';
import { FoldedSearchHighlight } from './FoldedSearchHighlight';
import ParcaPickerNestedModal from './ParcaPickerNestedModal';
import { getParcaFotoUrl } from '../services/productsService';
import {
  useSiparisParcaCatalog,
  getContentName,
  getContentCode,
} from '../hooks/useSiparisParcaCatalog';
import './SiparisParcaPicker.css';
import './SiparisParcaWizardPanel.css';

const ParcaThumb = ({ parca }) => {
  const src = getParcaFotoUrl(parca?.fotoUrl);
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="siparis-parca-picker__thumb"
        loading="lazy"
      />
    );
  }
  return (
    <span className="siparis-parca-picker__thumb-placeholder" title="Görsel yok">
      <i className="fas fa-image" aria-hidden />
    </span>
  );
};

/**
 * TP Sipariş Oluştur parça adımı: kategori üst modalda; katalog | seçilenler aynı ekranda.
 */
const SiparisParcaWizardPanel = ({
  showPrice = true,
  secilenParcalar = [],
  onAddPart,
  birimFiyatFromParca,
  renderSelectedPanel,
  eslesenUrunIcerikId = null,
}) => {
  const [lastAddedId, setLastAddedId] = useState(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const catalog = useSiparisParcaCatalog({
    secilenParcalar,
    enableDraftFilter: false,
    eslesenUrunIcerikId,
  });
  const {
    loading,
    selectedCategoryId,
    setSelectedCategoryId,
    clearCategoryFilter,
    categoryTree,
    expandedIds,
    toggleExpand,
    selectCategory,
    selectedCategoryLabel,
    parcaArama,
    setParcaArama,
    parcaAramaDebounced,
    filteredParcalar,
    selectedPartIds,
  } = catalog;

  const handleAdd = useCallback(
    (parca) => {
      const id = String(parca.id || parca._id);
      onAddPart?.(parca, 1);
      setLastAddedId(id);
      window.setTimeout(() => setLastAddedId(null), 600);
    },
    [onAddPart],
  );

  const hasCategoryFilter = Boolean(selectedCategoryId);

  const pickCategory = useCallback(
    (cid) => {
      if (cid) selectCategory(cid);
      else clearCategoryFilter();
      setCategoryModalOpen(false);
    },
    [selectCategory, clearCategoryFilter],
  );

  const renderCategoryNode = (node, depth = 0) => {
    const cid = node.cid;
    const hasChildren = (node.children || []).length > 0;
    const isExpanded = expandedIds[cid] === true;
    const isSelected = selectedCategoryId === cid;

    return (
      <div key={cid} className="siparis-parca-picker__cat-node" style={{ paddingLeft: depth * 10 }}>
        <div
          className={`siparis-parca-picker__cat-row ${isSelected ? 'is-selected' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => pickCategory(cid)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              pickCategory(cid);
            }
          }}
        >
          {hasChildren ? (
            <button
              type="button"
              className="siparis-parca-picker__cat-toggle"
              onClick={(e) => toggleExpand(cid, e)}
              aria-label={isExpanded ? 'Daralt' : 'Genişlet'}
            >
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
            </button>
          ) : (
            <span className="siparis-parca-picker__cat-toggle-spacer" />
          )}
          <span className="siparis-parca-picker__cat-name">{node.kategoriAdi || '—'}</span>
        </div>
        {hasChildren && isExpanded && (
          <div className="siparis-parca-picker__cat-children">
            {node.children.map((ch) => renderCategoryNode(ch, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="siparis-parca-wizard__loading">
        <i className="fas fa-spinner fa-spin" aria-hidden /> Parçalar yükleniyor...
      </div>
    );
  }

  return (
    <div className="siparis-parca-wizard">
      <div className="siparis-parca-wizard__toolbar">
        <div className="siparis-parca-wizard__filter-group">
          <button
            type="button"
            className={`siparis-parca-wizard__filter-btn ${!hasCategoryFilter ? 'is-active' : ''}`}
            onClick={clearCategoryFilter}
          >
            Tümü
          </button>
          <button
            type="button"
            className={`siparis-parca-wizard__filter-btn siparis-parca-wizard__filter-btn--category ${
              hasCategoryFilter ? 'is-active' : ''
            }`}
            onClick={() => setCategoryModalOpen(true)}
            title={hasCategoryFilter ? selectedCategoryLabel : 'Kategori seç'}
          >
            <i className="fas fa-filter siparis-parca-wizard__filter-btn-icon" aria-hidden />
            <span className="siparis-parca-wizard__filter-btn-label">
              {hasCategoryFilter ? selectedCategoryLabel : 'Kategori'}
            </span>
          </button>
        </div>
        <div className="siparis-parca-wizard__search-wrap">
          <i className="fas fa-search search-icon" aria-hidden />
          <input
            type="text"
            className="siparis-parca-wizard__search-input"
            placeholder="Parça adı veya kod ile ara..."
            value={parcaArama}
            onChange={(e) => setParcaArama(e.target.value)}
            maxLength={100}
          />
        </div>
      </div>

      <div className="siparis-parca-wizard__split">
        <section className="siparis-parca-wizard__catalog" aria-label="Parça kataloğu">
          <div className="siparis-parca-wizard__panel-header">
            <span
              className="siparis-parca-wizard__panel-title"
              title={hasCategoryFilter ? selectedCategoryLabel : undefined}
            >
              {hasCategoryFilter ? selectedCategoryLabel : 'Tüm parçalar'}
            </span>
            <span className="siparis-parca-wizard__count">{filteredParcalar.length} kayıt</span>
          </div>
          <div className="siparis-parca-wizard__table-wrap">
            {filteredParcalar.length === 0 ? (
              <div className="siparis-parca-wizard__empty">Parça bulunamadı</div>
            ) : (
              <table
                className={`admins-table siparis-parca-wizard__table${
                  showPrice
                    ? ' siparis-parca-wizard__table--with-price'
                    : ' siparis-parca-wizard__table--no-price'
                }`}
              >
                <colgroup>
                  <col className="siparis-parca-picker__col-thumb" />
                  <col />
                  <col />
                  {showPrice ? <col /> : null}
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th aria-label="Görsel" />
                    <th>Parça</th>
                    <th>Kod</th>
                    {showPrice && <th style={{ textAlign: 'right' }}>Net fiyat</th>}
                    <th className="siparis-parca-wizard__th-action" />
                  </tr>
                </thead>
                <tbody>
                  {filteredParcalar.map((p) => {
                    const pid = String(p.id || p._id);
                    const birim =
                      showPrice && birimFiyatFromParca ? birimFiyatFromParca(p) : null;
                    const isAdded = selectedPartIds.has(pid);
                    return (
                      <tr
                        key={pid}
                        className={isAdded ? 'siparis-parca-wizard__row--added' : ''}
                      >
                        <td className="siparis-parca-picker__cell-thumb">
                          <ParcaThumb parca={p} />
                        </td>
                        <td title={getContentName(p)} className="siparis-parca-wizard__cell-name">
                          <FoldedSearchHighlight
                            text={getContentName(p)}
                            queryRaw={parcaAramaDebounced}
                          />
                          {isAdded ? (
                            <span className="siparis-parca-wizard__added-badge">Eklendi</span>
                          ) : null}
                        </td>
                        <td title={getContentCode(p)} className="siparis-parca-wizard__cell-code">
                          <FoldedSearchHighlight
                            text={getContentCode(p)}
                            queryRaw={parcaAramaDebounced}
                          />
                        </td>
                        {showPrice && (
                          <td
                            className="siparis-parca-wizard__cell-price"
                            style={{ textAlign: 'right' }}
                          >
                            {birim != null ? `${birim.toFixed(2)} ₺` : '—'}
                          </td>
                        )}
                        <td className="siparis-parca-wizard__td-action">
                          <button
                            type="button"
                            className="category-action-btn siparis-parca-wizard__add-btn"
                            onClick={() => handleAdd(p)}
                            title="Ekle"
                          >
                            <i className="fas fa-plus" aria-hidden />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="siparis-parca-wizard__selected" aria-label="Seçilen parçalar">
          <div className="siparis-parca-wizard__panel-header">
            <span>Seçilenler ({secilenParcalar.length})</span>
          </div>
          <div className="siparis-parca-wizard__selected-body">
            {renderSelectedPanel?.(lastAddedId)}
          </div>
        </section>
      </div>

      <ParcaPickerNestedModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        title="Kategori seç"
        footerLabel="Kapat"
        size="category"
      >
        <div className="siparis-parca-wizard__cat-modal">
          <div className="siparis-parca-picker__cat-toolbar">
            <button
              type="button"
              className={`siparis-parca-picker__cat-all ${!hasCategoryFilter ? 'is-selected' : ''}`}
              onClick={() => pickCategory(null)}
            >
              <i className="fas fa-layer-group" aria-hidden />
              Tüm parçalar
            </button>
          </div>
          <div className="siparis-parca-wizard__cat-modal-tree">
            {categoryTree.map((node) => renderCategoryNode(node))}
          </div>
        </div>
      </ParcaPickerNestedModal>
    </div>
  );
};

export default SiparisParcaWizardPanel;
